import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { MEETING_STATUS, beYearOptions, currentBeYear, fmtDateTH, fmtTime } from './aoShared'

const emptyForm = { mt_title: '', mt_date: '', mt_time_start: '', mt_time_end: '', mt_place: '' }

// ธุรการ: ทะเบียนการประชุม
export default function Meetings() {
  const navigate = useNavigate()
  const [year, setYear] = useState(String(currentBeYear()))
  const [q, setQ] = useState('')
  const [meetings, setMeetings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/meetings?${params.toString()}`)
      if (res.data.success) setMeetings(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year, q])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = () => {
    setForm({ ...emptyForm, mt_date: new Date().toLocaleDateString('en-CA') })
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const res = await axiosInstance.post('/api/ao/meetings', form)
      if (res.data.success) {
        setShowModal(false)
        const { mt_id, mt_full_no } = res.data.data
        const go = await Swal.fire({
          icon: 'success',
          title: `สร้างการประชุมครั้งที่ ${mt_full_no} เรียบร้อย`,
          text: 'ไปจัดวาระและเชิญผู้เข้าประชุมต่อเลยหรือไม่?',
          showCancelButton: true,
          confirmButtonText: 'จัดวาระ/เชิญประชุม',
          cancelButtonText: 'ปิด',
        })
        if (go.isConfirmed) navigate(`/admin-office/meetings/${mt_id}`)
        else fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-team-line me-2 text-primary" />
              งานประชุม
            </h4>
            <div className="d-flex gap-2">
              <Button as={Link} to="/admin-office/resolutions" variant="outline-primary" size="sm">
                <i className="ri-task-line me-1" />ติดตามมติ
              </Button>
              <Button variant="primary" size="sm" onClick={openModal}>
                <i className="ri-add-line me-1" />สร้างการประชุม
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 240 }} placeholder="ค้นหา ครั้งที่/ชื่อการประชุม"
                value={q} onChange={e => setQ(e.target.value)} />
              <div className="ms-auto text-muted fs-sm">{meetings.length} รายการ</div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : meetings.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบการประชุมในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 90 }}>ครั้งที่</th>
                      <th>ชื่อการประชุม</th>
                      <th style={{ width: 170 }}>วัน-เวลา</th>
                      <th>สถานที่</th>
                      <th className="text-center" style={{ width: 90 }}>ผู้เข้าประชุม</th>
                      <th className="text-center" style={{ width: 90 }}>มติค้าง</th>
                      <th style={{ width: 100 }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meetings.map(m => {
                      const st = MEETING_STATUS[m.mt_status] || MEETING_STATUS.scheduled
                      return (
                        <tr key={m.mt_id} style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin-office/meetings/${m.mt_id}`)}>
                          <td className="fw-semibold">{m.mt_full_no}</td>
                          <td className="text-truncate" style={{ maxWidth: 260 }} title={m.mt_title}>
                            <Link to={`/admin-office/meetings/${m.mt_id}`} onClick={e => e.stopPropagation()}>{m.mt_title}</Link>
                          </td>
                          <td>{fmtDateTH(m.mt_date)}{m.mt_time_start ? ` ${fmtTime(m.mt_time_start)}` : ''}</td>
                          <td>{m.mt_place || '-'}</td>
                          <td className="text-center">{m.invitee_count}</td>
                          <td className="text-center">
                            {m.resolution_open_count > 0
                              ? <Badge bg="warning">{m.resolution_open_count}</Badge>
                              : (m.resolution_count > 0 ? <Badge bg="success">ครบ</Badge> : '-')}
                          </td>
                          <td><Badge bg={st.bg}>{st.label}</Badge></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">สร้างการประชุม (ออกครั้งที่อัตโนมัติ)</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ชื่อการประชุม <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={500} required value={form.mt_title}
                placeholder="เช่น ประชุมครูและบุคลากร ประจำเดือน..." autoFocus
                onChange={e => setForm(f => ({ ...f, mt_title: e.target.value }))} />
            </Form.Group>
            <Row className="g-2 mb-2">
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">วันที่ <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="date" required value={form.mt_date}
                  onChange={e => setForm(f => ({ ...f, mt_date: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">เวลาเริ่ม</Form.Label>
                <Form.Control size="sm" type="time" value={form.mt_time_start}
                  onChange={e => setForm(f => ({ ...f, mt_time_start: e.target.value }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">เวลาสิ้นสุด</Form.Label>
                <Form.Control size="sm" type="time" value={form.mt_time_end}
                  onChange={e => setForm(f => ({ ...f, mt_time_end: e.target.value }))} />
              </Col>
            </Row>
            <Form.Group>
              <Form.Label className="fs-sm mb-1">สถานที่</Form.Label>
              <Form.Control size="sm" type="text" maxLength={255} value={form.mt_place}
                placeholder="เช่น ห้องประชุมใหญ่ (จองห้องได้ที่เมนูจองห้องประชุม/รถ)"
                onChange={e => setForm(f => ({ ...f, mt_place: e.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-team-line me-1" />}
              สร้างการประชุม
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
